import type {
  AnnouncementSwatch,
  ApplicationCredentialPublic,
  ApplicationPublic,
  ApplicationTagPublic,
  GrantPublic,
  UpdateApplicationRequest,
  UserPublic,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  checkApplicationNow,
  createApplication,
  createGrant,
  issueApplicationCredential,
  listApplicationTags,
  listApplications,
  listGrants,
  listUsers,
  removeApplicationLogo,
  revokeAllApplicationCredentials,
  revokeApplicationCredential,
  revokeGrant,
  updateApplication,
  uploadApplicationLogo,
} from '../../lib/api';
import { formatDate, formatDateTime } from '../../lib/format';
import { healthOf } from '../../lib/health';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../ui/Modal';
import {
  Callout,
  Card,
  CardHeader,
  Chip,
  Dot,
  EmptyRow,
  EmptyScreen,
  ListTruncationNotice,
  MasterDetailSkeleton,
  SwatchStrip,
} from '../../ui/Primitives';
import {
  buttonStyle,
  cardHeaderStyle,
  checkboxRowStyle,
  checkboxStyle,
  detailColumnStyle,
  detailNameStyle,
  detailPaneStyle,
  eyebrowStyle,
  fieldLabelStyle,
  grantRowStyle,
  hintStyle,
  listPaneHeaderStyle,
  listPaneStyle,
  listContainerStyle,
  listRowStyle,
  masterDetailStyle,
  modalButtonStyle,
  monoStyle,
  primaryButtonStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
  textFieldStyle,
  tileLabelStyle,
  tileStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';
import { GrantModal, IssuedCredentialModal, RevokeModal, TestConnectionForm } from './SharedModals';

type Dialog =
  | { kind: 'register' }
  | { kind: 'edit'; application: ApplicationPublic }
  | { kind: 'grant'; application: ApplicationPublic }
  | { kind: 'revoke'; grant: GrantPublic }
  | { kind: 'issuedSecret'; title: string; lead: string; application: ApplicationPublic; secret: string }
  | { kind: 'revokeCredential'; application: ApplicationPublic; credential: ApplicationCredentialPublic }
  | { kind: 'revokeAllCredentials'; application: ApplicationPublic };

const LIST_LIMIT = 200;

/** The server's own bound on the launcher tile description, mirrored as affordance. */
const DESCRIPTION_MAX = 280;
/** PNG ONLY — the operator withdrew SVG, and the server sniffs magic bytes regardless. */
const ACCEPTED_LOGO_TYPES = 'image/png';

/**
 * The Applications surface. R5 lives here: `applications.key` is immutable after create, so
 * the register modal warns that the key is permanent and the edit modal renders it locked
 * and NEVER sends it — `UpdateApplicationRequest` has no `key` field, and the server returns
 * a typed 400 `immutable_field` to anything that tries anyway.
 */
export function ApplicationsPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /*
   * The Launcher tile card's collapse state lives HERE, not in the card: the card is keyed
   * on its saved values so a landed save re-seeds its draft by remounting, and a save must
   * not slam an open card shut mid-session.
   *
   * COLLAPSED ON LOAD (U1, operator ruling). Plain component state on purpose — nothing
   * persists it, so every fresh visit starts closed and the card is opened from its own
   * header when somebody actually wants to edit presentation.
   */
  const [tileOpen, setTileOpen] = useState(false);

  const applicationsQuery = useQuery({
    queryKey: queryKeys.applications,
    queryFn: () => listApplications({ limit: LIST_LIMIT }),
  });
  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  /** The managed launcher tag vocabulary — the two tag slots pick from it, by id. */
  const tagsQuery = useQuery({
    queryKey: queryKeys.applicationTags,
    queryFn: listApplicationTags,
  });
  const tags = useMemo(() => tagsQuery.data?.data ?? [], [tagsQuery.data]);

  const applications = useMemo(
    () => applicationsQuery.data?.data ?? [],
    [applicationsQuery.data],
  );
  const users = usersQuery.data?.data ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return applications;
    return applications.filter((application) =>
      `${application.name} ${application.key} ${application.baseUrl}`.toLowerCase().includes(needle),
    );
  }, [applications, search]);

  const selected = useMemo(
    () => applications.find((application) => application.id === selectedId) ?? visible[0] ?? null,
    [applications, visible, selectedId],
  );

  const grantsQuery = useQuery({
    queryKey: queryKeys.grantsForApplication(selected?.id ?? '', false),
    queryFn: () => listGrants({ applicationId: selected?.id ?? '', includeRevoked: false }),
    enabled: selected !== null,
  });
  const grants = grantsQuery.data?.data ?? [];

  function refreshApplications() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.applications });
  }

  function refresh() {
    refreshApplications();
    void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
  }

  function close() {
    setDialog(null);
    setFormError(null);
  }

  /**
   * ONE call, two journeys. Standalone (the empty-state "Issue credential" button, or
   * "Rotate" on an application that already has one) it fires directly; chained from
   * `registerMutation.onSuccess` below it is the second half of REGISTER-THEN-ISSUE. Either
   * way success opens the shown-once secret dialog — never a silent success, because the
   * secret exists nowhere else to look up later.
   */
  const issueCredentialMutation = useMutation({
    mutationFn: (id: string) => issueApplicationCredential(id),
    onSuccess: (result) => {
      refresh();
      setSelectedId(result.application.id);
      const rotated = result.application.credentials.length > 1;
      setDialog({
        kind: 'issuedSecret',
        title: rotated
          ? STR.modals.issueCredential.titleRotated
          : STR.modals.issueCredential.titleIssued,
        lead: rotated
          ? STR.modals.issueCredential.leadRotated(result.application.name)
          : STR.modals.issueCredential.leadIssued(result.application.name),
        application: result.application,
        secret: result.secret,
      });
    },
    onError: (error) => {
      // The application (still) exists with no new credential — say so rather than letting a
      // failed issue read as a silent success. The empty-state / Rotate action stays exactly
      // where it was, because nothing about the application's credential list changed.
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  /**
   * REGISTER-THEN-ISSUE. The acceptance journey is one operator action, not two: register an
   * application and immediately receive its secret. Two server calls under the hood — if the
   * second one fails, `issueCredentialMutation.onError` above says so and the application is
   * left exactly as it is (registered, no credential, "Issue credential" still offered)
   * rather than this handler pretending the whole journey succeeded.
   */
  const registerMutation = useMutation({
    mutationFn: createApplication,
    onSuccess: (result) => {
      refresh();
      setSelectedId(result.application.id);
      close();
      showToast(STR.toast.applicationRegistered(result.application.name));
      issueCredentialMutation.mutate(result.application.id);
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      name,
      baseUrl,
      active,
    }: {
      id: string;
      name: string;
      baseUrl: string;
      active: boolean;
    }) => updateApplication(id, { name, baseUrl, active }),
    onSuccess: () => {
      refresh();
      close();
      showToast(STR.toast.applicationUpdated);
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const grantMutation = useMutation({
    mutationFn: ({ userId, applicationId }: { userId: string; applicationId: string }) =>
      createGrant({ userId, applicationId }),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(STR.toast.accessGranted(result.grant.user.name, result.grant.application.name));
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grant: GrantPublic) => revokeGrant(grant.id),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(
        STR.toast.accessRevoked(result.grant.user.name, result.grant.application.name),
        'warn',
      );
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const revokeCredentialMutation = useMutation({
    mutationFn: ({ applicationId, credentialId }: { applicationId: string; credentialId: string }) =>
      revokeApplicationCredential(applicationId, credentialId),
    onSuccess: () => {
      refreshApplications();
      close();
      showToast(STR.toast.credentialRevoked, 'warn');
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const revokeAllCredentialsMutation = useMutation({
    mutationFn: (id: string) => revokeAllApplicationCredentials(id),
    onSuccess: () => {
      refreshApplications();
      close();
      showToast(STR.toast.credentialsRevokedAll, 'warn');
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const checkNowMutation = useMutation({
    mutationFn: (id: string) => checkApplicationNow(id),
    onSuccess: () => {
      refreshApplications();
      showToast(STR.toast.applicationChecked);
    },
    onError: (error) => {
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const loading = applicationsQuery.isLoading;
  const noApplications = !loading && applications.length === 0;

  const grantableUsers = users.filter(
    (user: UserPublic) => user.active && !grants.some((grant) => grant.userId === user.id),
  );

  /*
   * D3 — collection-wide, from the server's own SQL, never counted off the capped page.
   * `summary.healthy/maintenance/unreachable` partition only the applications that HAVE
   * reported a status; one that has never been checked counts toward none of them, which is
   * the same "never checked claims nothing" rule the health chip follows (R3).
   */
  const summary = applicationsQuery.data?.meta.summary ?? null;
  const total = applicationsQuery.data?.meta.total ?? null;

  return (
    <AdminShell
      surfaceId="applications"
      {...(loading
        ? {}
        : {
            primary: {
              label: STR.surfaces.applications.primary,
              onClick: () => {
                setFormError(null);
                setDialog({ kind: 'register' });
              },
            },
          })}
    >
      {loading ? <MasterDetailSkeleton /> : null}

      {noApplications ? (
        <EmptyScreen
          title={STR.applications.emptyTitle}
          body={STR.applications.emptyBody}
          action={
            <button
              type="button"
              className="prim"
              style={modalButtonStyle}
              onClick={() => {
                setFormError(null);
                setDialog({ kind: 'register' });
              }}
            >
              {STR.applications.emptyAction}
            </button>
          }
        />
      ) : null}

      {!loading && !noApplications ? (
        <div style={masterDetailStyle}>
          <div style={listPaneStyle}>
            <div style={listPaneHeaderStyle}>
              {summary === null ? null : (
                <div className="tx-subtle" style={eyebrowStyle}>
                  {STR.applications.meta(
                    summary.active,
                    summary.healthy,
                    summary.maintenance,
                    summary.unreachable,
                  )}
                </div>
              )}
              <input
                className="field"
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder={STR.applications.searchPlaceholder}
                aria-label={STR.applications.searchLabel}
                style={searchFieldStyle}
              />
            </div>

            {visible.length === 0 ? (
              <div style={{ padding: '28px 14px', textAlign: 'center' }}>
                <div className="tx-subtle" style={{ fontSize: 12.5 }}>
                  {STR.applications.noneMatch}
                </div>
                <button
                  type="button"
                  className="tbtn"
                  onClick={() => {
                    setSearch('');
                  }}
                  style={{ ...rowActionStyle, marginTop: 7 }}
                >
                  {STR.common.clearSearch}
                </button>
              </div>
            ) : null}

            <ul style={listContainerStyle}>
              {visible.map((application) => {
                const health = healthOf(application.lastStatus);
                return (
                  <li key={application.id}>
                    <button
                      type="button"
                      className="li"
                      aria-current={selected?.id === application.id}
                      onClick={() => {
                        setSelectedId(application.id);
                      }}
                      style={listRowStyle}
                    >
                      <Dot tone={health.dot} />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                          {application.name}
                        </span>
                        <span
                          className="tx-subtle"
                          style={{ display: 'block', fontSize: 11.5, ...monoStyle }}
                        >
                          {application.key}
                        </span>
                      </span>
                      <Chip tone={health.chip} size="sm" style={{ whiteSpace: 'nowrap' }}>
                        {health.label}
                      </Chip>
                    </button>
                  </li>
                );
              })}
            </ul>

            <ListTruncationNotice loaded={applications.length} total={total} />
          </div>

          <div style={detailPaneStyle}>
            {selected === null ? (
              <p className="tx-subtle">{STR.applications.selectPrompt}</p>
            ) : (
              <ApplicationDetail
                application={selected}
                tags={tags}
                tileOpen={tileOpen}
                onToggleTile={() => {
                  setTileOpen((open) => !open);
                }}
                grants={grants}
                grantsLoading={grantsQuery.isLoading}
                // A deactivated application cannot be granted into at all — the server
                // refuses with 400 `inactive_target` — so the affordance goes with it.
                canGrant={selected.active && grantableUsers.length > 0}
                issuePending={issueCredentialMutation.isPending}
                checkNowPending={checkNowMutation.isPending}
                onEdit={() => {
                  setFormError(null);
                  setDialog({ kind: 'edit', application: selected });
                }}
                onGrant={() => {
                  setFormError(null);
                  setDialog({ kind: 'grant', application: selected });
                }}
                onRevoke={(grant) => {
                  setDialog({ kind: 'revoke', grant });
                }}
                onIssueOrRotate={() => {
                  issueCredentialMutation.mutate(selected.id);
                }}
                onRevokeCredential={(credential) => {
                  setDialog({ kind: 'revokeCredential', application: selected, credential });
                }}
                onRevokeAllCredentials={() => {
                  setDialog({ kind: 'revokeAllCredentials', application: selected });
                }}
                onCheckNow={() => {
                  checkNowMutation.mutate(selected.id);
                }}
              />
            )}
          </div>
        </div>
      ) : null}

      {dialog?.kind === 'register' ? (
        <RegisterApplicationModal
          error={formError}
          busy={registerMutation.isPending}
          onCancel={close}
          onSubmit={(values) => {
            if (values.key.trim() === '' || values.name.trim() === '' || values.baseUrl.trim() === '') {
              setFormError(STR.modals.registerApplication.required);
              return;
            }
            setFormError(null);
            registerMutation.mutate({
              key: values.key.trim().toLowerCase(),
              name: values.name.trim(),
              baseUrl: values.baseUrl.trim(),
            });
          }}
        />
      ) : null}

      {dialog?.kind === 'edit' ? (
        <EditApplicationModal
          application={dialog.application}
          error={formError}
          busy={updateMutation.isPending}
          onCancel={close}
          onSubmit={(values) => {
            if (values.name.trim() === '' || values.baseUrl.trim() === '') {
              setFormError(STR.modals.editApplication.required);
              return;
            }
            setFormError(null);
            updateMutation.mutate({
              id: dialog.application.id,
              name: values.name.trim(),
              baseUrl: values.baseUrl.trim(),
              active: values.active,
            });
          }}
        />
      ) : null}

      {dialog?.kind === 'grant' ? (
        <GrantModal
          context={STR.modals.grant.contextForApplication(dialog.application.name)}
          label={STR.modals.grant.pickPerson}
          options={grantableUsers.map((user) => ({
            id: user.id,
            label: `${user.name} — ${user.email}`,
          }))}
          error={formError}
          busy={grantMutation.isPending}
          onCancel={close}
          onSubmit={(userId) => {
            if (userId === '') {
              setFormError(STR.modals.grant.required);
              return;
            }
            grantMutation.mutate({ userId, applicationId: dialog.application.id });
          }}
        />
      ) : null}

      {dialog?.kind === 'revoke' ? (
        <RevokeModal
          what={dialog.grant.application.name}
          who={dialog.grant.user.name}
          busy={revokeMutation.isPending}
          onCancel={close}
          onSubmit={() => {
            revokeMutation.mutate(dialog.grant);
          }}
        />
      ) : null}

      {dialog?.kind === 'issuedSecret' ? (
        <IssuedCredentialModal
          title={dialog.title}
          lead={dialog.lead}
          application={dialog.application}
          secret={dialog.secret}
          onClose={close}
        />
      ) : null}

      {dialog?.kind === 'revokeCredential' ? (
        <Modal
          title={STR.modals.revokeCredential.title(dialog.application.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={revokeCredentialMutation.isPending}
                onClick={() => {
                  revokeCredentialMutation.mutate({
                    applicationId: dialog.application.id,
                    credentialId: dialog.credential.id,
                  });
                }}
              >
                {STR.modals.revokeCredential.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.revokeCredential.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}

      {dialog?.kind === 'revokeAllCredentials' ? (
        <Modal
          title={STR.modals.revokeAllCredentials.title(dialog.application.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={revokeAllCredentialsMutation.isPending}
                onClick={() => {
                  revokeAllCredentialsMutation.mutate(dialog.application.id);
                }}
              >
                {STR.modals.revokeAllCredentials.submit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.modals.revokeAllCredentials.body}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}
    </AdminShell>
  );
}

function ApplicationDetail({
  application,
  tags,
  tileOpen,
  onToggleTile,
  grants,
  grantsLoading,
  canGrant,
  issuePending,
  checkNowPending,
  onEdit,
  onGrant,
  onRevoke,
  onIssueOrRotate,
  onRevokeCredential,
  onRevokeAllCredentials,
  onCheckNow,
}: {
  application: ApplicationPublic;
  tags: ApplicationTagPublic[];
  tileOpen: boolean;
  onToggleTile: () => void;
  grants: GrantPublic[];
  grantsLoading: boolean;
  canGrant: boolean;
  issuePending: boolean;
  checkNowPending: boolean;
  onEdit: () => void;
  onGrant: () => void;
  onRevoke: (grant: GrantPublic) => void;
  onIssueOrRotate: () => void;
  onRevokeCredential: (credential: ApplicationCredentialPublic) => void;
  onRevokeAllCredentials: () => void;
  onCheckNow: () => void;
}) {
  const health = healthOf(application.lastStatus);

  return (
    <div style={detailColumnStyle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={detailNameStyle}>{application.name}</div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}
          >
            <code
              className="c-slate"
              style={{
                ...monoStyle,
                fontSize: 12,
                padding: '2px 8px',
                borderRadius: 6,
                borderWidth: 1,
                borderStyle: 'solid',
              }}
            >
              {application.key}
            </code>
            <span
              className="tx-subtle"
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
              }}
            >
              {STR.applications.keyLocked}
            </span>
            <Chip tone={application.active ? 'positive' : 'neutral'}>
              {application.active ? STR.applications.activeLabel : STR.applications.inactiveLabel}
            </Chip>
          </div>
        </div>
        <button
          type="button"
          className="ghost"
          style={{ ...buttonStyle, marginLeft: 'auto' }}
          onClick={onEdit}
        >
          {STR.applications.edit}
        </button>
      </div>

      <Callout
        tone="neutral"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '16px 18px',
          borderRadius: 12,
        }}
      >
        <span
          className={`dot-${health.dot}`}
          style={{ width: 9, height: 9, borderRadius: 999, marginTop: 5, flex: 'none' }}
          aria-hidden="true"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 650 }}>{health.label}</div>
          <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.55 }}>{health.note}</div>
        </div>
        {/* Beside the fact it refreshes, so the relationship between the button and the
            health/Last checked tile below it is obvious. */}
        <button
          type="button"
          className="tbtn"
          style={{ ...quietButtonStyle, flex: 'none', alignSelf: 'center' }}
          disabled={checkNowPending}
          onClick={onCheckNow}
        >
          {checkNowPending ? STR.common.loading : STR.applications.checkNow}
        </button>
      </Callout>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
        <div style={{ ...tileStyle, gridColumn: 'span 2' }}>
          <div className="tx-subtle" style={tileLabelStyle}>
            {STR.applications.baseAddress}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginTop: 4,
              ...monoStyle,
              overflowWrap: 'anywhere',
            }}
          >
            {application.baseUrl}
          </div>
        </div>
        <div style={tileStyle}>
          <div className="tx-subtle" style={tileLabelStyle}>
            {STR.applications.version}
          </div>
          <div
            style={{ fontSize: 14, fontWeight: 600, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}
          >
            {application.lastVersion ?? STR.common.dash}
          </div>
        </div>
        <div style={tileStyle}>
          <div className="tx-subtle" style={tileLabelStyle}>
            {STR.applications.lastChecked}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
            {formatDateTime(application.lastCheckedAt)}
          </div>
        </div>
      </div>

      {/* Keyed on the saved tile values, exactly like Settings' branding editor: a landed
          save remounts this card with a clean draft, which is how the seed happens without
          a setState inside an effect. The collapse state lives in the parent, so it
          survives that remount. */}
      <LauncherTileCard
        key={`${application.id}:${application.description ?? ''}:${application.tag1Id ?? ''}:${application.tag2Id ?? ''}:${application.pillColor ?? ''}:${application.logoUrl ?? ''}`}
        application={application}
        tags={tags}
        open={tileOpen}
        onToggle={onToggleTile}
      />

      <Card>
        <CardHeader
          title={STR.applications.credentialTitle}
          action={
            application.credentials.length > 0 ? (
              <button
                type="button"
                className="tbtn"
                style={quietButtonStyle}
                disabled={issuePending}
                onClick={onIssueOrRotate}
              >
                {STR.applications.rotateCredential}
              </button>
            ) : undefined
          }
        />
        {application.credentials.length === 0 ? (
          <EmptyRow>
            <div>{STR.applications.credentialEmptyBody}</div>
            <button
              type="button"
              className="prim"
              style={{ ...modalButtonStyle, marginTop: 12 }}
              disabled={issuePending}
              onClick={onIssueOrRotate}
            >
              {STR.applications.issueCredential}
            </button>
          </EmptyRow>
        ) : (
          <>
            <div style={{ padding: '12px 18px 0' }}>
              {/* THE ROTATION RULE, stated plainly: issuing never stops a secret, only
                  revoking does — so overlapping active credentials during a cutover are the
                  intended state, not a mistake. */}
              <Callout tone="neutral">{STR.applications.rotateNote}</Callout>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {application.credentials.map((credential) => (
                <li key={credential.id} className="row" style={grantRowStyle}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {STR.applications.credentialIssued(formatDateTime(credential.createdAt))}
                    </div>
                    <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                      {credential.lastUsedAt === null
                        ? STR.applications.credentialNeverUsed
                        : STR.applications.credentialLastUsed(
                            formatDateTime(credential.lastUsedAt),
                          )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tbtn tbtn-danger"
                    style={rowActionStyle}
                    onClick={() => {
                      onRevokeCredential(credential);
                    }}
                  >
                    {STR.common.revoke}
                  </button>
                </li>
              ))}
            </ul>
            {application.credentials.length > 1 ? (
              <div
                style={{
                  padding: '10px 18px',
                  background: 'var(--surface-2)',
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  className="dbtn"
                  style={rowActionStyle}
                  onClick={onRevokeAllCredentials}
                >
                  {STR.applications.revokeAllCredentials}
                </button>
              </div>
            ) : null}
            <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)' }}>
              <div className="tx-subtle" style={{ ...tileLabelStyle, marginBottom: 8 }}>
                {STR.applications.testConnection}
              </div>
              <TestConnectionForm applicationId={application.id} />
            </div>
          </>
        )}
      </Card>

      <Card>
        <CardHeader
          title={STR.applications.holdersTitle}
          aside={
            <span
              className="tx-subtle"
              style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 400 }}
            >
              {grantsLoading ? STR.common.dash : grants.length}
            </span>
          }
          action={
            canGrant ? (
              <button type="button" className="tbtn" style={quietButtonStyle} onClick={onGrant}>
                {STR.applications.grantToPerson}
              </button>
            ) : undefined
          }
        />
        {!grantsLoading && grants.length === 0 ? (
          <EmptyRow>{STR.applications.noHolders}</EmptyRow>
        ) : null}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {grants.map((grant) => (
            <li key={grant.id} className="row" style={grantRowStyle}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{grant.user.name}</div>
                <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                  {grant.user.email}
                </div>
              </div>
              <div className="tx-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                {STR.grants.grantedOn(formatDate(grant.grantedAt))}
              </div>
              <button
                type="button"
                className="tbtn tbtn-danger"
                style={rowActionStyle}
                onClick={() => {
                  onRevoke(grant);
                }}
              >
                {STR.common.revoke}
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/**
 * THE LAUNCHER TILE CARD — the one new section on this screen. Everything else here
 * (header, status callout, meta tiles, Credential, the Edit dialog, People with access) is
 * unchanged.
 *
 * Tile content is edited INLINE, with an explicit Save, rather than in the Edit dialog: the
 * dialog owns identity (name, address, active) and this owns presentation, and the two have
 * different blast radii.
 *
 * THE LOGO IS NOT PART OF THE DIRTY FORM. It is its own pair of endpoints and therefore
 * applies IMMEDIATELY — exactly as Settings → Branding's does. Pretending otherwise would
 * mean holding an uploaded file in the browser and lying about when it took effect.
 *
 * PNG ONLY, in both the picker's `accept` and the helper copy. The server accepts nothing
 * else and sniffs the magic bytes rather than trusting the declared type, so a renamed file
 * is refused there whatever this control allows.
 *
 * THE APPLICATION'S PILL COLOUR LIVES HERE TOO (U2, operator ruling). It used to be a
 * whole-section map in Settings; it is now `pillColor`, a column on this application's own
 * row, written through the SAME true-patch PATCH as the description and the tags — one
 * application, one place. A TYPE's colour is a different fact with a different home and is
 * untouched by this card.
 */
function LauncherTileCard({
  application,
  tags,
  open,
  onToggle,
}: {
  application: ApplicationPublic;
  tags: ApplicationTagPublic[];
  open: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(application.description ?? '');
  const [tag1Id, setTag1Id] = useState(application.tag1Id ?? '');
  const [tag2Id, setTag2Id] = useState(application.tag2Id ?? '');
  const [pillColor, setPillColor] = useState<AnnouncementSwatch | null>(application.pillColor);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.applications });
    // The launcher renders exactly these fields, from its own endpoint — keep it honest.
    void queryClient.invalidateQueries({ queryKey: queryKeys.myApplications });
    /*
     * The pill colour is joined onto every shape that renders this application's chip, and
     * each one is a separate read: the admin announcements list and the launcher's own
     * (`announcement.application.color`), and both sessions surfaces (`apps[].color`). A
     * colour that changed here and not there would be the same fact rendering two ways.
     */
    void queryClient.invalidateQueries({ queryKey: queryKeys.announcements });
    void queryClient.invalidateQueries({ queryKey: queryKeys.myAnnouncements });
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminSessions });
    void queryClient.invalidateQueries({ queryKey: queryKeys.mySessions });
  }

  const saveMutation = useMutation({
    mutationFn: (patch: UpdateApplicationRequest) => updateApplication(application.id, patch),
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.tileSaved);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadApplicationLogo(application.id, file),
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.logoUpdated);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  const removeLogoMutation = useMutation({
    mutationFn: () => removeApplicationLogo(application.id),
    onSuccess: () => {
      invalidate();
      setError(null);
      showToast(STR.toast.logoRemoved, 'warn');
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  /*
   * `description` is compared TRIMMED here because that is what `save()` sends. Comparing
   * the raw value instead made whitespace alone count as a change: typing a trailing space
   * enabled Save, `save()` then trimmed it back to the stored value, built an empty patch,
   * and the server refused with "Supply at least one of…" — an error on an edit the person
   * never made. The dirty test and the patch test must read the same value.
   */
  const dirty =
    description.trim() !== (application.description ?? '') ||
    tag1Id !== (application.tag1Id ?? '') ||
    tag2Id !== (application.tag2Id ?? '') ||
    pillColor !== application.pillColor;

  /**
   * A RETIRED tag stays on an application already carrying it — only NEW assignment is
   * refused — so a retired tag appears in a slot's options exactly when that slot already
   * holds it, marked as retired. Every other option is live. And a tag never appears in
   * both slots: the server refuses that, and so does the picker.
   */
  function optionsFor(slot: string, other: string): ApplicationTagPublic[] {
    return tags.filter(
      (tag) => (tag.retiredAt === null || tag.id === slot) && tag.id !== other,
    );
  }

  function save() {
    const trimmed = description.trim();
    if (trimmed.length > DESCRIPTION_MAX) {
      setError(STR.applications.tile.descriptionTooLong(DESCRIPTION_MAX));
      return;
    }
    if (tag1Id !== '' && tag1Id === tag2Id) {
      setError(STR.applications.tile.sameTag);
      return;
    }
    // A TRUE PATCH — an unchanged slot is not re-sent. That matters beyond tidiness: a
    // retired tag already carried would be refused if it were sent as a fresh assignment.
    const patch: UpdateApplicationRequest = {};
    if (trimmed !== (application.description ?? '')) patch.description = trimmed;
    if (tag1Id !== (application.tag1Id ?? '')) patch.tag1Id = tag1Id === '' ? null : tag1Id;
    if (tag2Id !== (application.tag2Id ?? '')) patch.tag2Id = tag2Id === '' ? null : tag2Id;
    if (pillColor !== application.pillColor) patch.pillColor = pillColor;
    saveMutation.mutate(patch);
  }

  const overBudget = description.trim().length > DESCRIPTION_MAX;

  return (
    <Card>
      <div style={{ ...cardHeaderStyle, borderBottom: open ? '1px solid var(--border)' : 'none' }}>
        <span>{STR.applications.tile.title}</span>
        <span className="tx-subtle" style={{ fontSize: 11.5, fontWeight: 400 }}>
          {STR.applications.tile.subtitle}
        </span>
        <button
          type="button"
          className="tbtn"
          aria-expanded={open}
          style={{ ...rowActionStyle, marginLeft: 'auto' }}
          onClick={onToggle}
        >
          {open ? STR.applications.tile.collapse : STR.applications.tile.expand}
        </button>
      </div>

      {open ? (
        <>
          <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error === null ? null : (
              <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
                {error}
              </Callout>
            )}

            <div>
              <span className="tx-muted" style={fieldLabelStyle}>
                {STR.applications.tile.logoLabel}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {/* The dashed striped slot means "no asset yet" — an ADMIN-side affordance
                    only. Where a logo IS set it is painted at `contain`, so it scales to fit
                    and never crops or stretches, exactly as the launcher shows it. */}
                <span
                  className="logo-slot"
                  style={{ overflow: 'hidden', display: 'grid', placeItems: 'center' }}
                  aria-hidden="true"
                >
                  {application.logoUrl === null ? null : (
                    <span
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        backgroundColor: 'var(--surface-2)',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundImage: `url("${application.logoUrl}")`,
                      }}
                    />
                  )}
                </span>
                <button
                  type="button"
                  className="ghost"
                  style={buttonStyle}
                  disabled={uploadMutation.isPending}
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  {application.logoUrl === null
                    ? STR.applications.tile.chooseLogo
                    : STR.applications.tile.replaceLogo}
                </button>
                {application.logoUrl === null ? null : (
                  <button
                    type="button"
                    className="tbtn tbtn-danger"
                    style={rowActionStyle}
                    disabled={removeLogoMutation.isPending}
                    onClick={() => {
                      removeLogoMutation.mutate();
                    }}
                  >
                    {STR.applications.tile.removeLogo}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_LOGO_TYPES}
                  style={{ display: 'none' }}
                  aria-hidden="true"
                  tabIndex={-1}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file !== undefined) uploadMutation.mutate(file);
                  }}
                />
              </div>
              <span className="tx-subtle" style={hintStyle}>
                {STR.applications.tile.logoHint}
              </span>
            </div>

            <label style={{ display: 'block' }}>
              <span
                className="tx-muted"
                style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
              >
                {STR.applications.tile.descriptionLabel}{' '}
                <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                  {STR.settings.optional}
                </span>
                <span
                  className={overBudget ? 'tx-warn-strong' : 'tx-subtle'}
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {STR.applications.tile.descriptionCount(
                    description.trim().length,
                    DESCRIPTION_MAX,
                  )}
                </span>
              </span>
              <textarea
                className={overBudget ? 'field field-over' : 'field'}
                rows={3}
                value={description}
                placeholder={STR.applications.tile.descriptionPlaceholder}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setError(null);
                }}
                style={{ ...textFieldStyle, fontSize: 13, lineHeight: 1.55, resize: 'vertical' }}
              />
              <span className="tx-subtle" style={hintStyle}>
                {STR.applications.tile.descriptionHint}
              </span>
            </label>

            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'block' }}>
                  <span
                    className="tx-muted"
                    style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
                  >
                    {STR.applications.tile.tag1Label}{' '}
                    <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                      {STR.settings.optional}
                    </span>
                  </span>
                  <select
                    className="field"
                    value={tag1Id}
                    onChange={(event) => {
                      setTag1Id(event.target.value);
                      setError(null);
                    }}
                    style={{ ...textFieldStyle, fontSize: 13, padding: '9px 30px 9px 11px' }}
                  >
                    <option value="">{STR.applications.tile.noTag}</option>
                    {optionsFor(tag1Id, tag2Id).map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.retiredAt === null
                          ? tag.name
                          : STR.applications.tile.retiredTagOption(tag.name)}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'block' }}>
                  <span
                    className="tx-muted"
                    style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'baseline', gap: 6 }}
                  >
                    {STR.applications.tile.tag2Label}{' '}
                    <span className="tx-subtle" style={{ fontSize: 11, fontWeight: 500 }}>
                      {STR.settings.optional}
                    </span>
                  </span>
                  <select
                    className="field"
                    value={tag2Id}
                    onChange={(event) => {
                      setTag2Id(event.target.value);
                      setError(null);
                    }}
                    style={{ ...textFieldStyle, fontSize: 13, padding: '9px 30px 9px 11px' }}
                  >
                    <option value="">{STR.applications.tile.noTag}</option>
                    {optionsFor(tag2Id, tag1Id).map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.retiredAt === null
                          ? tag.name
                          : STR.applications.tile.retiredTagOption(tag.name)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <span className="tx-subtle" style={hintStyle}>
                {STR.applications.tile.tagsHint}
              </span>
            </div>

            {/* THE APPLICATION'S OWN COLOUR (U2). The SAME ten-swatch strip the announcement
                type form draws — `SwatchStrip` reading `ANNOUNCEMENT_SWATCHES` from the
                contract, so /web never spells the list out and the two controls cannot
                drift. Clicking edits the DRAFT; nothing recolours a live chip until Save. */}
            <div>
              <span className="tx-muted" style={fieldLabelStyle}>
                {STR.applications.tile.colorLabel}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Chip
                  tone="neutral"
                  swatch={pillColor}
                  style={{ minWidth: 86, justifyContent: 'center' }}
                  {...(pillColor === null ? { title: STR.applications.tile.noColor } : {})}
                >
                  <span className="ptr">{application.name}</span>
                </Chip>
                {/*
                 * NULLABLE here, and only here: an application may legitimately carry no
                 * color, so "no color" is the strip's first slot and picking it writes an
                 * explicit `pillColor: null` through the same true-patch Save the rest of
                 * this card uses. The announcement-type strip passes the flag off — a
                 * type's color is what makes it legible on every announcement carrying it,
                 * so "none" is not a meaningful state there.
                 */}
                <SwatchStrip
                  nullable
                  value={pillColor}
                  label={STR.applications.tile.colorGroupLabel(application.name)}
                  onPick={(swatch) => {
                    setPillColor(swatch);
                    setError(null);
                  }}
                />
              </div>
              <span className="tx-subtle" style={hintStyle}>
                {STR.applications.tile.colorHint}
              </span>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '12px 22px',
              borderTop: '1px solid var(--border)',
              background: 'var(--surface-2)',
              borderRadius: '0 0 11px 11px',
            }}
          >
            <button
              type="button"
              className="tbtn"
              style={quietButtonStyle}
              disabled={!dirty}
              onClick={() => {
                setDescription(application.description ?? '');
                setTag1Id(application.tag1Id ?? '');
                setTag2Id(application.tag2Id ?? '');
                setPillColor(application.pillColor);
                setError(null);
                showToast(STR.toast.tileDiscarded, 'warn');
              }}
            >
              {STR.applications.tile.reset}
            </button>
            {dirty ? (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Chip tone="attention">{STR.settings.unsaved}</Chip>
                <button
                  type="button"
                  className="prim"
                  style={primaryButtonStyle}
                  disabled={saveMutation.isPending}
                  onClick={save}
                >
                  {saveMutation.isPending ? STR.settings.saving : STR.settings.save}
                </button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </Card>
  );
}

function RegisterApplicationModal({
  error,
  busy,
  onCancel,
  onSubmit,
}: {
  error: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (values: { key: string; name: string; baseUrl: string }) => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://');

  return (
    <Modal
      title={STR.modals.registerApplication.title}
      lead={STR.modals.registerApplication.lead}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={busy}
            onClick={() => {
              onSubmit({ key, name, baseUrl });
            }}
          >
            {STR.modals.registerApplication.submit}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <label style={{ display: 'block' }}>
          <span
            className="tx-muted"
            style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <span>{STR.modals.registerApplication.keyLabel}</span>
            <Chip
              tone="attention"
              size="sm"
              style={{ padding: '2px 8px', letterSpacing: '.05em', textTransform: 'uppercase' }}
            >
              {STR.modals.registerApplication.keyPermanent}
            </Chip>
          </span>
          <input
            className="field"
            type="text"
            value={key}
            placeholder={STR.modals.registerApplication.keyPlaceholder}
            onChange={(event) => {
              setKey(event.target.value);
            }}
            style={{ ...textFieldStyle, ...monoStyle }}
          />
          <span className="tx-subtle" style={hintStyle}>
            {STR.modals.registerApplication.keyHint}
          </span>
        </label>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.registerApplication.nameLabel}
          </span>
          <input
            className="field"
            type="text"
            value={name}
            placeholder={STR.modals.registerApplication.namePlaceholder}
            onChange={(event) => {
              setName(event.target.value);
            }}
            style={textFieldStyle}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.registerApplication.urlLabel}
          </span>
          <input
            className="field"
            type="text"
            value={baseUrl}
            placeholder={STR.modals.registerApplication.urlPlaceholder}
            onChange={(event) => {
              setBaseUrl(event.target.value);
            }}
            style={{ ...textFieldStyle, ...monoStyle }}
          />
        </label>
      </ModalForm>
    </Modal>
  );
}

/**
 * R5 — the key is rendered, locked, and never part of the payload. There is no input for it
 * and `updateApplication` cannot carry one, so the immutable field cannot be sent by
 * accident from here at all.
 */
function EditApplicationModal({
  application,
  error,
  busy,
  onCancel,
  onSubmit,
}: {
  application: ApplicationPublic;
  error: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (values: { name: string; baseUrl: string; active: boolean }) => void;
}) {
  const [name, setName] = useState(application.name);
  const [baseUrl, setBaseUrl] = useState(application.baseUrl);
  const [active, setActive] = useState(application.active);

  return (
    <Modal
      title={STR.modals.editApplication.title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={busy}
            onClick={() => {
              onSubmit({ name, baseUrl, active });
            }}
          >
            {STR.common.saveChanges}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '11px 13px',
            borderRadius: 9,
            border: '1px solid var(--border)',
            background: 'var(--surface-2)',
          }}
        >
          <div>
            <div
              className="tx-subtle"
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
              }}
            >
              {STR.modals.editApplication.keyLabel}
            </div>
            <code style={{ ...monoStyle, fontSize: 14, fontWeight: 650 }}>{application.key}</code>
          </div>
          <Chip
            tone="neutral"
            style={{
              padding: '3px 10px',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
            }}
          >
            {STR.modals.editApplication.keyLocked}
          </Chip>
        </div>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.editApplication.nameLabel}
          </span>
          <input
            className="field"
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            style={textFieldStyle}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {STR.modals.editApplication.urlLabel}
          </span>
          <input
            className="field"
            type="text"
            value={baseUrl}
            onChange={(event) => {
              setBaseUrl(event.target.value);
            }}
            style={{ ...textFieldStyle, ...monoStyle }}
          />
        </label>
        <label style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => {
              setActive(event.target.checked);
            }}
            style={checkboxStyle}
          />
          <span>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
              {STR.modals.editApplication.activeTitle}
            </span>
            <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
              {STR.modals.editApplication.activeNote}
            </span>
          </span>
        </label>
      </ModalForm>
    </Modal>
  );
}
